import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogActions, DialogBody, DialogTitle } from "src/components/base/dialog";
import { ErrorMessage, Field, FieldGroup, Fieldset, RequiredLabel } from "src/components/base/fieldset";
import Form from "src/components/base/form";
import { useForm } from "@tanstack/react-form";
import { Button, PrimaryButton } from "src/components/base/button";
import { Input } from "src/components/base/input";
import { ApiInternal } from "src/api/api_internal";
import { isFormError } from "src/utils/form-error";

/**
 * The properties for {@link CreateOidcProviderDialog}
 */
export type CreateOidcProviderDialogProps = {
    /** Whether the dialog is open */
    isOpen: boolean;
    /** Callback when the provider is created */
    onCreate: (provider_id: string) => void;
    /** Callback when the popup should be closed */
    onClose: () => void;
};

/**
 * A dialog to create a new OIDC provider
 */
export default function CreateOidcProviderDialog(props: CreateOidcProviderDialogProps) {
    const [t] = useTranslation("dialog-create-oidc-provider");
    const [tg] = useTranslation();

    const form = useForm({
        defaultValues: {
            name: "",
        },
        validators: {
            onSubmitAsync: async ({ value }) => {
                const res = await ApiInternal.oidcProvider.create({ name: value.name });

                if (isFormError(res)) {
                    return {
                        fields: {
                            name: res.error.name_already_exist ? t("error.name-already-exist") : undefined,
                        },
                    };
                }

                props.onCreate(res.uuid);
            },
        },
    });

    useEffect(() => {
        form.reset();
    }, [props.isOpen]);

    return (
        <Dialog open={props.isOpen} onClose={props.onClose}>
            <DialogTitle>{t("heading.create-oidc-provider")}</DialogTitle>
            <DialogBody>
                <Form onSubmit={form.handleSubmit}>
                    <Fieldset>
                        <FieldGroup>
                            <form.Field name={"name"}>
                                {(fieldApi) => (
                                    <Field>
                                        <RequiredLabel>{t("label.name")}</RequiredLabel>
                                        <Input
                                            required={true}
                                            autoComplete={"off"}
                                            autoFocus={true}
                                            maxLength={255}
                                            value={fieldApi.state.value}
                                            onChange={(e) => fieldApi.handleChange(e.target.value)}
                                            invalid={fieldApi.state.meta.errors.length > 0}
                                        />
                                        {fieldApi.state.meta.errors.map((error) => (
                                            <ErrorMessage key={String(error)}>{error}</ErrorMessage>
                                        ))}
                                    </Field>
                                )}
                            </form.Field>

                            <DialogActions>
                                <Button plain={true} onClick={() => props.onClose()}>
                                    {tg("button.cancel")}
                                </Button>
                                <PrimaryButton type={"submit"}>{t("button.create")}</PrimaryButton>
                            </DialogActions>
                        </FieldGroup>
                    </Fieldset>
                </Form>
            </DialogBody>
        </Dialog>
    );
}
